import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { useSnackBar } from '../../../contexts/snackbar';

interface PDFViewerDialogProps {
  open: boolean;
  onClose: () => void;
  pdfUrl: string | null;
  title?: string;
  filename?: string;
}

/**
 * Reusable PDF Viewer Dialog Component
 * Displays a PDF in an iframe with download and print options
 */
export function PDFViewerDialog({
  open,
  onClose,
  pdfUrl,
  title = 'PDF Preview',
  filename = 'document.pdf',
}: PDFViewerDialogProps) {
  const { showMessage } = useSnackBar();

  // Cleanup blob URL when dialog closes
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleDownload = () => {
    if (!pdfUrl) return;

    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    showMessage('PDF downloaded successfully', 'success');
  };

  const handlePrint = () => {
    if (!pdfUrl) return;

    // Open PDF in new window and trigger print dialog
    const printWindow = window.open(pdfUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              flexGrow: 1,
            }}
            title="PDF Preview"
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleDownload}
          startIcon={<DownloadIcon />}
          variant="outlined"
          color="primary"
          disabled={!pdfUrl}
        >
          Save to device
        </Button>
        <Button
          onClick={handlePrint}
          startIcon={<PrintIcon />}
          variant="contained"
          color="primary"
          disabled={!pdfUrl}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PDFViewerDialog;




